import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, GripVertical } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import {
  canDeleteLeadStage,
  protectedLeadStageDeleteReason,
} from "@/lib/lead-stage-protection";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface Stage {
  id: string;
  name: string;
  slug: string;
  color: string;
  order_index: number;
  is_system: boolean;
  auto_disable_agent: boolean;
}

interface StageManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  funnelId?: string | null;
  onStagesUpdate: () => void;
  canManage?: boolean;
}

const colorOptions = [
  { value: "bg-gray-500", label: "Cinza" },
  { value: "bg-blue-500", label: "Azul" },
  { value: "bg-green-500", label: "Verde" },
  { value: "bg-yellow-500", label: "Amarelo" },
  { value: "bg-orange-500", label: "Laranja" },
  { value: "bg-red-500", label: "Vermelho" },
  { value: "bg-purple-500", label: "Roxo" },
  { value: "bg-pink-500", label: "Rosa" },
  { value: "bg-indigo-500", label: "Índigo" },
  { value: "bg-teal-500", label: "Turquesa" },
];

function SortableStageItem({
  stage,
  onEdit,
  onDelete,
  canManage,
}: {
  stage: Stage;
  onEdit: (stage: Stage) => void;
  onDelete: (id: string) => void;
  canManage: boolean;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: stage.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 border rounded-lg bg-card"
    >
      <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>
      <div className={`w-3 h-3 rounded-full ${stage.color}`} />
      <span className="flex-1 font-medium">{stage.name}</span>
      {stage.auto_disable_agent && (
        <Badge variant="outline" className="text-[10px] shrink-0">
          IA off
        </Badge>
      )}
      <Button variant="ghost" size="icon" onClick={() => onEdit(stage)} disabled={!canManage}>
        <Pencil className="w-4 h-4" />
      </Button>
      {!canDeleteLeadStage(stage) ? (
        <Button
          variant="ghost"
          size="icon"
          disabled
          title={protectedLeadStageDeleteReason(stage.slug)}
          className="text-muted-foreground/40 cursor-not-allowed"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      ) : (
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onDelete(stage.id)}
          className="text-destructive hover:text-destructive"
          disabled={!canManage}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}

export function StageManagerDialog({
  open,
  onOpenChange,
  workspaceId,
  funnelId,
  onStagesUpdate,
  canManage = true,
}: StageManagerDialogProps) {
  const [stages, setStages] = useState<Stage[]>([]);
  const [editingStage, setEditingStage] = useState<Stage | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    color: "bg-gray-500",
    auto_disable_agent: false,
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<
    { id: string; slug: string; name: string; leadsCount: number } | null
  >(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBatchRunning, setIsBatchRunning] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const { toast } = useToast();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (open) {
      fetchStages();
    }
  }, [open, workspaceId, funnelId]);

  const fetchStages = async () => {
    try {
      let query = supabase
        .from("lead_stages")
        .select("*")
        .eq("workspace_id", workspaceId)
        .order("order_index", { ascending: true });

      if (funnelId) {
        query = query.eq("funnel_id", funnelId);
      }

      const { data, error } = await query;

      if (error) throw error;
      setStages(
        (data || []).map((s) => ({
          ...s,
          auto_disable_agent: s.auto_disable_agent ?? false,
        })),
      );
    } catch (error: any) {
      toast({
        title: "Erro ao carregar estágios",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    if (!canManage) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = stages.findIndex((s) => s.id === active.id);
    const newIndex = stages.findIndex((s) => s.id === over.id);

    const newStages = [...stages];
    const [movedStage] = newStages.splice(oldIndex, 1);
    newStages.splice(newIndex, 0, movedStage);

    setStages(newStages);

    try {
      const updates = newStages.map((stage, index) => ({
        id: stage.id,
        order_index: index,
      }));

      for (const update of updates) {
        const { error } = await supabase
          .from("lead_stages")
          .update({ order_index: update.order_index })
          .eq("id", update.id);

        if (error) throw error;
      }

      toast({ title: "Ordem dos estágios atualizada" });
      onStagesUpdate();
    } catch (error: any) {
      toast({
        title: "Erro ao reordenar estágios",
        description: error.message,
        variant: "destructive",
      });
      fetchStages();
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Nome obrigatório",
        description: "Por favor, insira um nome para o estágio.",
        variant: "destructive",
      });
      return;
    }

    const newSlug = formData.name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");

    try {
      if (editingStage) {
        // Preserve slug on rename: leads/memberships stay attached to this stage
        // via the immutable slug. Only the display name/color/flags change.
        const { error } = await supabase
          .from("lead_stages")
          .update({
            name: formData.name,
            color: formData.color,
            auto_disable_agent: formData.auto_disable_agent,
          })
          .eq("id", editingStage.id);

        if (error) throw error;

        toast({ title: "Estágio atualizado com sucesso" });
      } else {
        const { error } = await supabase.from("lead_stages").insert({
          workspace_id: workspaceId,
          name: formData.name,
          slug: newSlug,
          color: formData.color,
          order_index: stages.length,
          is_system: false,
          funnel_id: funnelId ?? null,
          auto_disable_agent: formData.auto_disable_agent,
        });

        if (error) throw error;
        toast({ title: "Estágio criado com sucesso" });
      }

      setFormData({ name: "", color: "bg-gray-500", auto_disable_agent: false });
      setEditingStage(null);
      setIsCreating(false);
      fetchStages();
      onStagesUpdate();
    } catch (error: any) {
      toast({
        title: "Erro ao salvar estágio",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsBatchRunning(false);
    }
  };

  const checkLeadsInStage = async (stageSlug: string): Promise<number> => {
    try {
      const stage = stages.find((s) => s.id === stageSlug);
      if (!stage) return 0;

      let query = supabase
        .from("leads")
        .select("id", { count: "estimated", head: true })
        .eq("workspace_id", workspaceId)
        .eq("stage", stage.slug);

      if (funnelId) {
        query = query.eq("funnel_id", funnelId);
      }

      const { count, error } = await query;

      if (error) throw error;
      return count || 0;
    } catch (error) {
      console.error("Erro ao verificar leads no estágio:", error);
      return 0;
    }
  };

  const handleDeleteClick = async (id: string) => {
    if (stages.length <= 1) {
      toast({
        title: "Não é possível excluir",
        description: "Você precisa ter pelo menos um estágio no pipeline.",
        variant: "destructive",
      });
      return;
    }

    const stage = stages.find((s) => s.id === id);
    if (!stage) return;

    if (!canDeleteLeadStage(stage)) {
      toast({
        title: "Não é possível excluir",
        description: protectedLeadStageDeleteReason(stage.slug),
        variant: "destructive",
      });
      return;
    }

    const leadsCount = await checkLeadsInStage(id);

    setPendingDelete({ id, slug: stage.slug, name: stage.name, leadsCount });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);

    try {
      // 1) Se houver leads na coluna, excluí-los primeiro com o mesmo escopo
      //    usado em checkLeadsInStage (workspace + stage slug [+ funnel_id]).
      if (pendingDelete.leadsCount > 0) {
        let leadsQuery = supabase
          .from("leads")
          .delete()
          .eq("workspace_id", workspaceId)
          .eq("stage", pendingDelete.slug);

        if (funnelId) {
          leadsQuery = leadsQuery.eq("funnel_id", funnelId);
        }

        const { error: leadsError } = await leadsQuery;
        if (leadsError) throw leadsError;
      }

      // 2) Excluir o estágio.
      const { error, count } = await supabase
        .from("lead_stages")
        .delete({ count: "exact" })
        .eq("id", pendingDelete.id);

      if (error) throw error;

      if (!count) {
        toast({
          title: "Não foi possível excluir",
          description: pendingDelete.slug
            ? protectedLeadStageDeleteReason(pendingDelete.slug)
            : "Este estágio é obrigatório no pipeline e não pode ser removido.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title:
          pendingDelete.leadsCount > 0
            ? `Coluna e ${pendingDelete.leadsCount} lead(s) excluídos`
            : "Estágio excluído com sucesso",
      });
      fetchStages();
      onStagesUpdate();
    } catch (error: any) {
      toast({
        title: "Erro ao excluir estágio",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setDeleteConfirmOpen(false);
      setPendingDelete(null);
    }
  };

  const handleEdit = (stage: Stage) => {
    setEditingStage(stage);
    setFormData({
      name: stage.name,
      color: stage.color,
      auto_disable_agent: stage.auto_disable_agent,
    });
    setIsCreating(true);
    // Scroll the editor into view and focus the name input on next tick,
    // since the panel renders below a long list of stages.
    setTimeout(() => {
      editorRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      nameInputRef.current?.focus();
      nameInputRef.current?.select();
    }, 50);
  };

  const handleCancel = () => {
    setEditingStage(null);
    setFormData({ name: "", color: "bg-gray-500", auto_disable_agent: false });
    setIsCreating(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && isBatchRunning) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[calc(100dvh-2rem)] flex flex-col gap-4 overflow-hidden p-4 sm:p-6">
        <DialogHeader className="shrink-0 pr-8">
          <DialogTitle>Gerenciar Estágios do Pipeline</DialogTitle>
          <DialogDescription>
            Personalize os estágios do seu pipeline de leads. Arraste para reordenar.
            Apenas Atendimento Humano é obrigatório; os demais estágios podem ser excluídos
            por gestores e administradores.
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain pr-1">
          <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {stages.map((stage) => (
                  <SortableStageItem
                    key={stage.id}
                    stage={stage}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    canManage={canManage}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {isCreating ? (
            <div
              ref={editorRef}
              className="space-y-4 p-4 border-2 border-primary/40 rounded-lg bg-muted/50 scroll-mt-4"
            >
              <div className="text-sm font-semibold text-primary">
                {editingStage ? `Editando: ${editingStage.name}` : "Novo estágio"}
              </div>
              <div className="space-y-2">
                <Label htmlFor="stage-name">Nome do Estágio</Label>
                <Input
                  id="stage-name"
                  ref={nameInputRef}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Em Negociação"
                  disabled={!canManage}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="stage-color">Cor</Label>
                <Select value={formData.color} onValueChange={(value) => setFormData({ ...formData, color: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {colorOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${option.value}`} />
                          {option.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div className="space-y-1">
                  <Label htmlFor="auto-disable-agent">Desligar IA ao entrar neste estágio</Label>
                  <p className="text-xs text-muted-foreground">
                    Pausa conversas abertas e cancela follow-ups automaticamente.
                  </p>
                </div>
                <Switch
                  id="auto-disable-agent"
                  checked={formData.auto_disable_agent}
                  onCheckedChange={(checked) =>
                    setFormData((prev) => ({ ...prev, auto_disable_agent: checked }))
                  }
                  disabled={!canManage}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} className="flex-1" disabled={isBatchRunning || !canManage}>
                  {isBatchRunning ? `Processando... (${batchProgress})` : editingStage ? "Atualizar" : "Criar"}
                </Button>
                <Button onClick={handleCancel} variant="outline" className="flex-1" disabled={isBatchRunning}>
                  Cancelar
                </Button>
              </div>
            </div>
          ) : canManage ? (
            <Button onClick={() => setIsCreating(true)} variant="outline" className="w-full">
              <Plus className="w-4 h-4 mr-2" />
              Adicionar Estágio
            </Button>
          ) : null}
        </div>
      </DialogContent>

      <AlertDialog
        open={deleteConfirmOpen}
        onOpenChange={(next) => {
          if (isDeleting) return;
          setDeleteConfirmOpen(next);
          if (!next) setPendingDelete(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingDelete && pendingDelete.leadsCount > 0
                ? `Excluir coluna e ${pendingDelete.leadsCount} lead(s)?`
                : "Confirmar exclusão"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && pendingDelete.leadsCount > 0 ? (
                <>
                  A coluna <strong>{pendingDelete.name}</strong> possui{" "}
                  <strong>{pendingDelete.leadsCount} lead(s)</strong>. Ao
                  excluir o estágio, todos esses leads também serão removidos
                  permanentemente. Esta ação não pode ser desfeita.
                </>
              ) : (
                "Tem certeza que deseja excluir este estágio? Esta ação não pode ser desfeita."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteConfirm();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting
                ? "Excluindo..."
                : pendingDelete && pendingDelete.leadsCount > 0
                ? `Excluir coluna e ${pendingDelete.leadsCount} lead(s)`
                : "Excluir"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
